import { Collection } from 'firestorter';
import Collections from '../../utils/collections';
import feedType from '../context/FeedContext/config/feedTypes';
import CoachActivity, { activityStatus } from './CoachActivity';

/**
 * What the task is about. The PRD's "category". Values must match the
 * server-side CoachTaskType enum (S2-7981). The task's subject is the `user` field:
 * GENERIC has no client (user = coach); client-directed types set user = the client.
 * Consumers detect a client-directed task via `user !== coach`.
 */
const CoachTaskType = {
  GENERIC: 'GENERIC',
  SEND_MESSAGE: 'SEND_MESSAGE',
  SEND_VOICE_NOTE: 'SEND_VOICE_NOTE',
  SEND_VIDEO: 'SEND_VIDEO',
  SOCIAL_MEDIA_POST: 'SOCIAL_MEDIA_POST',
};

/** Drives notification routing on the server. URGENT → immediate push. */
const CoachTaskPriority = {
  URGENT: 'URGENT',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
};

// Which types are offered depends on whether the task is tied to a client (its subject).
// A client-less task (subject = coach) is GENERIC or a coach-level social post; the
// send-message/voice/video types are meaningless without a client to send to.
// SOCIAL_MEDIA_POST is in both: it can be a coach-level post or a client repost.
const NO_CLIENT_TASK_TYPES = [
  CoachTaskType.GENERIC,
  CoachTaskType.SOCIAL_MEDIA_POST,
];
const CLIENT_DIRECTED_TASK_TYPES = [
  CoachTaskType.SEND_MESSAGE,
  CoachTaskType.SEND_VOICE_NOTE,
  CoachTaskType.SEND_VIDEO,
  CoachTaskType.SOCIAL_MEDIA_POST,
];
// The client-directed types that reply into a chat (excludes SOCIAL_MEDIA_POST, which isn't a chat reply).
const CHAT_REPLY_TASK_TYPES = [
  CoachTaskType.SEND_MESSAGE,
  CoachTaskType.SEND_VOICE_NOTE,
  CoachTaskType.SEND_VIDEO,
];

// Firestore caps an `in` filter at 10 values, so coach lists are queried in chunks.
const COACH_IN_CHUNK_SIZE = 10;

/**
 * A coach task is a coachActivity doc with type === COACH_TASK. CoachTask extends
 * CoachActivity (same collection) and adds task behavior only — the display getters
 * live on CoachActivity so the feed (which builds CoachActivity instances) renders
 * tasks directly. Completing/removing a task from a feed item needs no CoachTask
 * instance: use the inherited `updateFields` on the CoachActivity.
 */
class CoachTask extends CoachActivity {
  /**
   * Creates a coach task by writing a coachActivity doc directly (the Reminders
   * precedent). Notifications + analytics are fired by a server-side onCreate
   * trigger (S2-7982), not here.
   * @param {object} taskData coach, user (the subject — the client for client-directed
   *   tasks, the coach for generic), assignedBy, assignedByName, taskType, taskTitle,
   *   priority, deadline, instructions?, attachments?
   * @returns {Promise<import('firestorter').Document>} the created document
   */
  static async create(taskData) {
    const coachActivityCollection = new Collection(Collections.COACH_ACTIVITY);
    const now = new Date();

    return coachActivityCollection.add({
      ...taskData,
      type: feedType.COACH_TASK,
      status: activityStatus.IN_PROGRESS,
      createdAt: now,
      time: now,
    });
  }

  /**
   * Marks this task complete: status → COMPLETED, stamps completedAt + completedBy.
   * The completer (e.g. an AC completing on the coach's behalf) can differ from the
   * assigner. `completedBy` is omitted when not provided (Firestore rejects undefined).
   */
  async markComplete(completedBy) {
    const fields = {
      status: activityStatus.COMPLETED,
      completedAt: new Date(),
    };
    if (completedBy) {
      fields.completedBy = completedBy;
    }
    return this.updateFields(fields);
  }

  /**
   * Soft-deletes this task: status → REMOVED, stamps removedAt + removedBy (CAM/AC delete).
   * The remover can differ from the assigner. `removedBy` is omitted when not provided.
   */
  async remove(removedBy) {
    const fields = {
      status: activityStatus.REMOVED,
      removedAt: new Date(),
    };
    if (removedBy) {
      fields.removedBy = removedBy;
    }
    return this.updateFields(fields);
  }

  /**
   * Updates the editable fields of a task in place (CAM/AC edit). Coach / assignedBy /
   * type ownership never change here — only the task's own details. Stamps updatedAt and
   * updatedBy (the editor can differ from the assigner; updatedBy omitted when absent).
   */
  async updateDetails({
    taskType,
    taskTitle,
    priority,
    deadline,
    instructions,
    attachments,
    updatedBy,
  }) {
    const fields = {
      taskType,
      taskTitle,
      priority,
      deadline,
      instructions,
      attachments,
      updatedAt: new Date(),
    };
    if (updatedBy) {
      fields.updatedBy = updatedBy;
    }
    return this.updateFields(fields);
  }

  /**
   * Live (real-time) coachActivity collections for a set of coaches' COACH_TASK docs,
   * chunked by the Firestore `in` limit. Status/overdue are derived on read (no status
   * filter in the query) so it reuses the existing (coach, type, …) index via
   * `coach in` + `type ==`. Kept live (initCollection, not a one-shot fetch) so the
   * coach-task admin context derives counts + per-coach lists reactively.
   */
  static async getTasksCollectionsForCoaches(coachIds = []) {
    const ids = [...new Set(coachIds)].filter(Boolean);
    const collections = [];
    for (let i = 0; i < ids.length; i += COACH_IN_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + COACH_IN_CHUNK_SIZE);
      collections.push(new Collection(Collections.COACH_ACTIVITY, {
        createDocument: ({ id }, opts) => new CoachTask(id, opts),
        query: (ref) => ref
          .where('type', '==', feedType.COACH_TASK)
          .where('coach', 'in', chunk),
      }));
    }
    await Promise.all(collections.map((collection) => CoachTask.initCollection(collection)));
    return collections;
  }
}

export default CoachTask;
export {
  CoachTaskType,
  CoachTaskPriority,
  NO_CLIENT_TASK_TYPES,
  CLIENT_DIRECTED_TASK_TYPES,
  CHAT_REPLY_TASK_TYPES,
};
